import { useState, useCallback } from 'react';
import { parseProductKey } from '../AppUtils.js';
import { isAdminMode } from '../constants.js';

/**
 * Hook to manage compulsory categories/subcategories
 * 
 * When a user removes an item from a compulsory category:
 * 1. The item is removed
 * 2. User is navigated to that category
 * 3. User cannot navigate away until they select a replacement
 */
export const useCompulsoryCategories = (selectedProducts, categoryConfig) => {
  // Store the compulsory requirements from the compatibility profile
  const [compulsoryCategories, setCompulsoryCategories] = useState([]);
  const [compulsorySubcategories, setCompulsorySubcategories] = useState([]);
  
  // Track if user is "locked" into selecting a compulsory item
  // Format: { type: 'category' | 'subcategory', name: string, message: string } | null
  const [pendingCompulsory, setPendingCompulsory] = useState(null);

  // Initialize compulsory data from baseConfig
  const initializeCompulsoryData = useCallback((baseConfig) => {
    if (!baseConfig) return;

    // Extract category names from the compulsory arrays
    const categories = (baseConfig.compulsoryCategories || []).map(cat => ({
      id: cat._id,
      name: cat.categoryName,
      order: cat.categoryOrder
    }));

    const subcategories = (baseConfig.compulsorySubcategories || []).map(sub => ({
      id: sub._id,
      name: sub.subCategoryName,
      parentCategory: sub.parentCategory
    }));

    setCompulsoryCategories(categories);
    setCompulsorySubcategories(subcategories);
  }, []);

  // Check if a category is compulsory
  const isCategoryCompulsory = useCallback((categoryName) => {
    if (isAdminMode()) return false;
    return compulsoryCategories.some(cat => cat.name === categoryName);
  }, [compulsoryCategories]);

  // Check if a subcategory is compulsory
  const isSubcategoryCompulsory = useCallback((subcategoryName) => {
    if (isAdminMode()) return false;
    return compulsorySubcategories.some(sub => sub.name === subcategoryName);
  }, [compulsorySubcategories]);

  // Check if a category or subcategory is compulsory
  const isCompulsory = useCallback((mainCategory, subCategory) => {
    if (isAdminMode()) return false;
    return isCategoryCompulsory(mainCategory) || isSubcategoryCompulsory(subCategory);
  }, [isCategoryCompulsory, isSubcategoryCompulsory]);

  // Check if a compulsory category has any products selected
  const hasCategorySelection = useCallback((categoryName) => {
    return Object.entries(selectedProducts).some(([key, product]) => {
      const { mainCategory } = parseProductKey(key.split('_')[0]);
      return mainCategory === categoryName;
    });
  }, [selectedProducts]);

  // Check if a compulsory subcategory has any products selected
  const hasSubcategorySelection = useCallback((subcategoryName) => {
    return Object.entries(selectedProducts).some(([key, product]) => {
      const baseKey = key.split('_')[0];
      const { subCategory } = parseProductKey(baseKey);
      return subCategory === subcategoryName;
    });
  }, [selectedProducts]);

  // Get all missing compulsory items (for submission validation)
  const getMissingCompulsoryItems = useCallback(() => {
    if (isAdminMode()) return [];

    const missing = [];

    // Check compulsory categories
    compulsoryCategories.forEach(cat => {
      if (!hasCategorySelection(cat.name)) {
        missing.push({
          type: 'category',
          name: cat.name,
          message: `Please select an item from ${cat.name}`
        });
      }
    });

    // Check compulsory subcategories
    compulsorySubcategories.forEach(sub => {
      if (!hasSubcategorySelection(sub.name)) {
        missing.push({
          type: 'subcategory',
          name: sub.name,
          message: `Please select a ${sub.name}`
        });
      }
    });

    return missing;
  }, [compulsoryCategories, compulsorySubcategories, hasCategorySelection, hasSubcategorySelection]);

  // Check if build is complete (all compulsory items selected)
  const isBuildComplete = useCallback(() => {
    return getMissingCompulsoryItems().length === 0;
  }, [getMissingCompulsoryItems]);

  // Called when a product is about to be removed
  // Returns { shouldBlock: boolean, navigateTo: { mainCategory, subCategory } | null }
  const handleCompulsoryRemoval = useCallback((productKey, product) => {
    if (isAdminMode()) {
      return { shouldBlock: false, navigateTo: null };
    }

    const baseKey = productKey.split('_')[0];
    const { mainCategory, subCategory } = parseProductKey(baseKey);

    // Check if this is from a compulsory category
    const categoryIsCompulsory = isCategoryCompulsory(mainCategory);
    const subcategoryIsCompulsory = isSubcategoryCompulsory(subCategory);

    if (!categoryIsCompulsory && !subcategoryIsCompulsory) {
      return { shouldBlock: false, navigateTo: null };
    }

    // Count how many items are in this category/subcategory
    let itemCount = 0;
    Object.keys(selectedProducts).forEach(key => {
      const keyBase = key.split('_')[0];
      const { mainCategory: keyMain, subCategory: keySub } = parseProductKey(keyBase);
      
      if (subcategoryIsCompulsory) {
        if (keySub === subCategory) itemCount++;
      } else if (categoryIsCompulsory) {
        if (keyMain === mainCategory) itemCount++;
      }
    });

    // If this is the last item in a compulsory category, set pending state
    if (itemCount <= 1) {
      const compulsoryInfo = subcategoryIsCompulsory
        ? { type: 'subcategory', name: subCategory, mainCategory }
        : { type: 'category', name: mainCategory, mainCategory };

      setPendingCompulsory(compulsoryInfo);

      return {
        shouldBlock: false, // Allow removal
        navigateTo: { mainCategory, subCategory },
        message: `Please select a replacement ${subcategoryIsCompulsory ? subCategory : 'item from ' + mainCategory}`
      };
    }

    return { shouldBlock: false, navigateTo: null };
  }, [isCategoryCompulsory, isSubcategoryCompulsory, selectedProducts]);

  // Check if navigation should be blocked (when there's a pending compulsory)
  const shouldBlockNavigation = useCallback((targetMainCategory, targetSubCategory) => {
    if (isAdminMode()) return false;
    if (!pendingCompulsory) return false;

    // Allow navigation within the same compulsory category
    if (pendingCompulsory.type === 'subcategory') {
      // Must stay in the exact subcategory
      return targetSubCategory !== pendingCompulsory.name;
    } else {
      // Must stay in the main category (any subcategory is fine)
      return targetMainCategory !== pendingCompulsory.name;
    }
  }, [pendingCompulsory]);

  // Called when a product is added - clears pending state if requirement is met
  const handleProductAdded = useCallback((mainCategory, subCategory) => {
    if (!pendingCompulsory) return;

    if (pendingCompulsory.type === 'subcategory' && subCategory === pendingCompulsory.name) {
      setPendingCompulsory(null);
    } else if (pendingCompulsory.type === 'category' && mainCategory === pendingCompulsory.name) {
      setPendingCompulsory(null);
    }
  }, [pendingCompulsory]);

  // Force clear pending state (for admin or edge cases)
  const clearPendingCompulsory = useCallback(() => {
    setPendingCompulsory(null);
  }, []);

  // Get compulsory status for UI indicators
  const getCompulsoryStatus = useCallback((mainCategory, subCategory) => {
    const categoryCompulsory = isCategoryCompulsory(mainCategory);
    const subcategoryCompulsory = isSubcategoryCompulsory(subCategory);

    if (!categoryCompulsory && !subcategoryCompulsory) {
      return { isCompulsory: false, isEmpty: false, isPending: false };
    }

    const hasSelection = subcategoryCompulsory 
      ? hasSubcategorySelection(subCategory)
      : hasCategorySelection(mainCategory);

    const isPending = pendingCompulsory && (
      (pendingCompulsory.type === 'subcategory' && pendingCompulsory.name === subCategory) ||
      (pendingCompulsory.type === 'category' && pendingCompulsory.name === mainCategory)
    );

    return {
      isCompulsory: true,
      isEmpty: !hasSelection,
      isPending: isPending
    };
  }, [isCategoryCompulsory, isSubcategoryCompulsory, hasCategorySelection, hasSubcategorySelection, pendingCompulsory]);

  return {
    // State
    compulsoryCategories,
    compulsorySubcategories,
    pendingCompulsory,
    
    // Initialization
    initializeCompulsoryData,
    
    // Checks
    isCategoryCompulsory,
    isSubcategoryCompulsory,
    isCompulsory,
    hasCategorySelection,
    hasSubcategorySelection,
    getMissingCompulsoryItems,
    isBuildComplete,
    getCompulsoryStatus,
    
    // Actions
    handleCompulsoryRemoval,
    handleProductAdded,
    shouldBlockNavigation,
    clearPendingCompulsory
  };
};
